import { useMemo } from "react";
import { marked } from "marked";
import AiSummaryFileIcon from "../../../../../../../../../../assets/svg/ai-summary-file-icon";
import AiSummaryIcon from "../../../../../../../../../../assets/svg/ai-summary-icon";
import HashtagIcon from "../../../../../../../../../../assets/svg/hashtag-icon";
import { getDateTimeByFormat } from "../../../../../../../../../../redux/slices/dateTimeFormatSlice";
import {
  markdownBreakFormatter,
  addTargetBlankToLinks,
  convertPhoneNumbersToLinks,
} from "../../../../../../../../../../utils/functions/formatters";
import { TicketData } from "../../../../../../utils/types";
import AiSummaryShimmer from "../AiSummaryShimmer";
import "./TicketAiSummary.css";

interface TicketAiSummaryProps {
  ticketId?: TicketData["ticket_id"];
  aiSummary?: TicketData["ai_summary"];
  isLoading?: boolean;
}

const TicketAiSummary = ({
  ticketId,
  aiSummary,
  isLoading,
}: TicketAiSummaryProps) => {
  const processedSummary = useMemo(() => {
    if (!aiSummary?.content) return "";
    const formatted = markdownBreakFormatter(
      aiSummary.content.replace(/\\n/g, "\n")
    );
    let html = String(marked(formatted));
    html = addTargetBlankToLinks(html);
    html = convertPhoneNumbersToLinks(html);
    return html;
  }, [aiSummary?.content]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center h-[50px] px-3 border-b border-border-gray dark:border-border-gray-dk shrink-0">
        <h3 className="text-base font-semibold text-verba-indigo dark:text-verba-indigo-dk">
          AI Summary
        </h3>
      </div>

      {isLoading ? (
        <AiSummaryShimmer />
      ) : aiSummary ? (
        <div className="flex flex-col overflow-y-auto">
          <div className="flex flex-col gap-2 px-3 pt-3">
            <div className="flex items-center gap-0.5">
              <HashtagIcon className="w-3.5 h-3.5 text-gray-placeholder dark:text-gray-placeholder-dk" />
              <span className="text-md text-verba-indigo dark:text-verba-indigo-dk">
                Ticket #{ticketId}
              </span>
            </div>
            {aiSummary?.updated_at && (
              <p className="text-xs text-heading-text dark:text-heading-text-dk font-medium">
                Last Updated on{" "}
                <span className="font-normal text-gray-placeholder dark:text-gray-placeholder-dk">
                  {getDateTimeByFormat(new Date(aiSummary?.updated_at))?.date},{" "}
                  {getDateTimeByFormat(new Date(aiSummary?.updated_at))?.time}
                </span>
              </p>
            )}
          </div>
          <div className="flex flex-col gap-4 py-4 border-b border-border-gray dark:border-border-gray-dk mx-3">
            <div
              className="html-refactor ai-summary-html text-md text-heading-text dark:text-heading-text-dk"
              dangerouslySetInnerHTML={{ __html: processedSummary }}
            />
          </div>
          <div className="flex items-center gap-1 px-3 py-4">
            <AiSummaryIcon className="w-[18px] h-[18px] text-verba-blue dark:text-verba-blue-dk shrink-0" />
            <p className="text-sm text-verba-indigo dark:text-verba-indigo-dk">
              AI summary updates every{" "}
              <span className="font-semibold">5 mins</span>
            </p>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-center h-full mb-10">
          <div className="flex flex-col items-center justify-center gap-2">
            <div className="border border-gray-25 dark:border-gray-25-dk rounded-[21px] p-3">
              <div className="border border-gray-50 dark:border-gray-50-dk rounded-[21px] p-3">
                <div className="border border-gray-100 dark:border-gray-100-dk rounded-[21px] p-3">
                  <AiSummaryFileIcon className="w-[40px] h-[40px] text-gray-150 dark:text-gray-150-dk" />
                </div>
              </div>
            </div>
            <span className="text-md text-data dark:text-data-dk font-medium">
              No AI Summary yet
            </span>
            <span className="text-md text-gray-placeholder dark:text-gray-placeholder-dk text-center">
              Your AI summary will show up here <br /> once it's created.
            </span>
            <div className="flex items-center gap-1 px-3 py-4">
              <AiSummaryIcon className="w-[18px] h-[18px] text-verba-blue dark:text-verba-blue-dk shrink-0" />
              <p className="text-sm text-verba-indigo dark:text-verba-indigo-dk">
                AI summary updates every{" "}
                <span className="font-semibold">5 mins</span>
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketAiSummary;
